import { useEffect, useRef, useState } from "react";
import { vocabularize } from "src/Common/vocab";
import { Avatar, Popover, fullName } from "../timeoff/timeOffShared";
import { woStatusUi } from "./woShared";

// Detail card shown when a WO chip is clicked. Mirrors the mockup's "WO #..."
// popover: status pill, customer, description, date, primary technician.
function WoCard({ wo, onClose, onOpen }) {
  const status = woStatusUi(wo.status);
  const primaryTech = wo.assignedTechs?.find((t) => t.roleId === 0)?.user || wo.assignedTechs?.[0]?.user || null;

  return (
    <div className="w-full">
      <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <span className="font-OpenSansSemiBold text-[14px] leading-5 tracking-[0.14px] text-[#1A2332]">
          WO #{wo.number}
        </span>
        <button onClick={onClose} className="shrink-0 opacity-70 hover:opacity-100">
          <img src="/images/common/close.svg" alt="Close" className="w-4 h-4" />
        </button>
      </div>
      <span
        className={`mt-2 inline-block rounded-[16px] px-2 py-0.5 text-center text-[12px] font-OpenSansSemiBold leading-[18px] tracking-[0.12px] ${status.chip}`}
      >
        {status.label}
      </span>

      <hr className="mt-3 border-t border-[#E2E8F0]" />

      {wo.customer?.companyName && (
        <div className="mt-3">
          <div className="font-OpenSansSemiBold text-[12px] uppercase leading-[18px] tracking-[0.12px] text-[#868DA6]">
            Customer
          </div>
          <div className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
            {wo.customer.companyName}
          </div>
        </div>
      )}

      {wo.description && (
        <div className="mt-3">
          <div className="font-OpenSansSemiBold text-[12px] uppercase leading-[18px] tracking-[0.12px] text-[#868DA6]">
            Description
          </div>
          <div className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] line-clamp-3">
            {wo.description}
          </div>
        </div>
      )}

      <div className="mt-3 flex items-start justify-between gap-3">
        <div>
          <div className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#868DA6] uppercase">Date</div>
          <div className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#1A2332]">
            {wo.startDateString || "—"}
          </div>
        </div>
        {primaryTech && (
          <div className="text-right">
            <div className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#868DA6] uppercase">
              Technician
            </div>
            <div className="flex items-center justify-end gap-1.5">
              <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#1A2332]">
                {fullName(primaryTech)}
              </span>
              <Avatar user={primaryTech} className="w-[20px] h-[20px]" />
            </div>
          </div>
        )}
      </div>

      </div>

      {onOpen && (
        <div className="flex justify-end gap-2 rounded-b-lg border-t border-[#E5E8F3] bg-[#F8F9FE] p-4">
          <button
            onClick={() => onOpen(wo)}
            className="rounded-lg border border-[#C9CEE2] bg-white px-3 py-1.5 text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansSemiBold text-[#1A2332] capitalize shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] hover:bg-gray-50"
          >
            Details
          </button>
        </div>
      )}
    </div>
  );
}

// A single work-order pill on the technicians grid. Clicking it opens the
// detail popover. `onOpen(wo)` navigates to the full WO (optional).
function WoChip({ wo, onOpen }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const inTrigger = ref.current && ref.current.contains(e.target);
      const inPopover = e.target.closest && e.target.closest("[data-timeoff-popover]");
      if (!inTrigger && !inPopover) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <>
      <div
        ref={ref}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 h-[26px] rounded-full px-2 cursor-pointer bg-[#DCFCE7] text-[#15803D]"
        title={vocabularize(`WO #${wo.number}`)}
      >
        <img src="/images/calendar/technicians/green_wo.svg" alt="" className="w-[10.75px] h-[9.25px] shrink-0" />
        <span className="truncate font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize">
          #{wo.number}
        </span>
      </div>
      {open && (
        <Popover anchorRef={ref}>
          <WoCard wo={wo} onClose={() => setOpen(false)} onOpen={onOpen} />
        </Popover>
      )}
    </>
  );
}

export default WoChip;
